import { FC, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Spinner,
} from "@capacit-com/lara-law-shared-ui";
import { UserPlus, X } from "lucide-react";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import useWorkspace from "@/hooks/useWorkspace";
import Workspace from "@/models/workspace/Workspace";
import { UserWithRole } from "@/models/users/User";
import { logger } from "@/lib/logger";
import OwnerPicker from "./OwnerPicker";

interface ClaimWorkspaceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  workspace: Workspace | null;
}

const ClaimWorkspaceDialog: FC<ClaimWorkspaceDialogProps> = ({
  isOpen,
  onClose,
  workspace,
}) => {
  const { claimWorkspace } = useWorkspace();
  const [selectedUser, setSelectedUser] = useState<UserWithRole | null>(null);

  const handleConfirm = async () => {
    if (!workspace || !selectedUser) return;

    try {
      await claimWorkspace.mutateAsync({
        workspaceId: workspace.id,
        newOwnerId: selectedUser.id,
      });
      showSuccessToast(
        `Workspace "${workspace.name}" er tildelt ny ejer`,
        "Tildelt"
      );
      handleClose();
    } catch (error) {
      logger.error("Failed to claim orphaned workspace", {
        operation: "claimOrphanedWorkspace",
        error,
        workspaceId: workspace.id,
        newOwnerId: selectedUser.id,
      });
      showErrorToast("Fejl ved tildeling af ejer. Prøv igen.", "Fejl");
    }
  };

  const handleClose = () => {
    setSelectedUser(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tildel ny ejer</DialogTitle>
          <DialogDescription>
            Vælg en bruger som skal blive ejer af workspace "{workspace?.name}".
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <OwnerPicker
            selectedUser={selectedUser}
            onSelectUser={setSelectedUser}
            disabled={claimWorkspace.isPending}
            isActive={isOpen}
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={claimWorkspace.isPending}
          >
            <X className="w-4 h-4 mr-1" />
            Annuller
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedUser || claimWorkspace.isPending}
          >
            {claimWorkspace.isPending ? (
              <Spinner className="text-primary-foreground" />
            ) : (
              <>
                <UserPlus className="w-4 h-4 mr-1" />
                Tildel ejer
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ClaimWorkspaceDialog;
